import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowRight, Check, QrCode, Camera, Clock } from 'lucide-react'
import { DARK, TEAL, BG, MarketingNavBar, MarketingFooter, FaqAccordion, faqJsonLd } from '../../components/MarketingLayout'

const FAQS = [
  {
    q: 'Kell hozzá speciális nyomtató vagy szkenner?',
    a: 'Nem. A QR-kód a normál címkére, a normál nyomtatóddal kerül rá — semmilyen extra hardver nem kell hozzá. A beolvasáshoz pedig bármelyik telefon kamerája elég, nem kell külön szkenner-kütyü.',
  },
  {
    q: 'Mit lát a vevő vagy az ellenőr, ha beolvassa a QR-kódot?',
    a: 'Egy publikus oldalt: a termék nevét, a gyártás dátumát, a fogyaszthatósági/lejárati dátumot, és hogy az egyes alapanyagok melyik beszállítótól, milyen tétel- vagy számlaszámmal érkeztek.',
  },
  {
    q: 'Kötelező ezt használni?',
    a: 'Nem jogszabályi kötelezettség egy konkrét QR-kódos megoldás — a nyomonkövethetőség maga (852/2004/EK) igen. A QR-kód egy kényelmi eszköz, ami könnyebbé teszi ennek a bemutatását, akár egy NÉBIH-ellenőrzésnél, akár egy érdeklődő vevőnek.',
  },
  {
    q: 'Mi az a számla-fotó tárolás, és miért külön bővítmény?',
    a: 'Ha szeretnéd, a beszállítói számlát is lefotózhatod és tárolhatod minden tételhez, amit aztán bármikor visszakereshetsz. Ez tárhelyet és feldolgozást igényel, ezért ez a rész külön, kis összegű havi bővítmény (990 Ft + ÁFA/hó) — az alap nyomonkövetés (tétel rögzítése, QR-generálás) enélkül is használható.',
  },
]

export default function NyomonkovetesQrKoddalPage() {
  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Helmet>
        <title>Alapanyag nyomonkövetés QR-kóddal — kütyük nélkül — CukrászDoc</title>
        <meta name="description" content="Hogyan valósítható meg a nyomonkövethetőség egy cukrászdában QR-kóddal, drága szkenner és nyomtató-kütyü nélkül? Lépésről lépésre bemutatjuk." />
        <link rel="canonical" href="https://cukraszdoc.hu/blog/alapanyag-nyomonkoveres-qr-koddal" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd(FAQS))}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: 'Alapanyag nyomonkövetés QR-kóddal — kütyük nélkül',
          datePublished: '2026-08-04',
          dateModified: '2026-08-04',
          author: { '@type': 'Organization', name: 'CukrászDoc' },
          publisher: { '@type': 'Organization', name: 'CukrászDoc' },
        })}</script>
      </Helmet>
      <MarketingNavBar />

      <section style={{ background: `linear-gradient(160deg, ${DARK} 0%, #162119 100%)`, paddingTop: '5rem' }} className="relative overflow-hidden">
        <div className="relative max-w-3xl mx-auto px-6 py-16 lg:py-20">
          <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ background: 'rgba(91,184,160,0.15)', color: TEAL, border: '1px solid rgba(91,184,160,0.3)' }}>
            <QrCode size={12} />
            Tudástár
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight text-white mb-5">
            Alapanyag nyomonkövetés <span style={{ color: TEAL }}>QR-kóddal, kütyük nélkül</span>
          </h1>
          <p className="text-lg leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
            A piacon kapható nyomonkövetési megoldások jó része drága nyomtató- és szkenner-hardverhez van kötve. Megmutatjuk, hogyan lehet ugyanezt elérni egy meglévő nyomtatóval és a vevő telefonjával.
          </p>
        </div>
        <div className="relative" style={{ height: 60, marginBottom: -2 }}>
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="absolute bottom-0 w-full h-full">
            <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="#ffffff" />
          </svg>
        </div>
      </section>

      <article className="bg-white py-16">
        <div className="max-w-3xl mx-auto px-6 flex flex-col gap-12">

          <div>
            <h2 className="text-2xl font-bold mb-4" style={{ color: DARK }}>Mi az a nyomonkövetés, és miért kérik egyre gyakrabban?</h2>
            <p className="text-base leading-relaxed text-gray-600 mb-4">
              A nyomonkövethetőség — vagyis hogy bármikor visszakereshető legyen, honnan jött egy alapanyag és mi készült belőle — a HACCP-alapú élelmiszerbiztonsági rendszer egyik alappillére (852/2004/EK). Arról írtunk korábban részletesen, <Link to="/blog/mit-jelent-a-haccp-igazolas" style={{ color: TEAL, fontWeight: 600 }}>mit jelent valójában a "HACCP igazolás"</Link> — a nyomonkövetés ennek a kötelezettségnek a napi szintű, gyakorlati megvalósítása.
            </p>
            <p className="text-base leading-relaxed text-gray-600">
              Egyre több NÉBIH-ellenőrzésen és egyre több vevőtől (különösen közétkeztetés, viszonteladók) érkezik a kérdés: vissza tudod-e vezetni egy adott terméket odáig, hogy melyik beszállítótól, mikor érkezett az alapanyag, amiből készült?
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold mb-4" style={{ color: DARK }}>A piaci megoldások gyakran drága hardverhez kötik</h2>
            <p className="text-base leading-relaxed text-gray-600">
              A legtöbb HACCP-szoftver, ami QR-kódos nyomonkövetést kínál, ezt saját címkenyomtatóhoz és szkenner-eszközhöz köti — ezek egyszeri beszerzési költsége könnyen 100 000 Ft fölé megy, plusz havidíj és címkénkénti költség. Pedig a QR-kód technikailag egy egyszerű kép, amit bármilyen nyomtatóval ki lehet nyomtatni, és bármelyik telefon kamerája el tudja olvasni — nincs szükség hozzá proprietary hardverre.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold mb-4" style={{ color: DARK }}>Hogyan működik a CukrászDoc-ban</h2>
            <div className="flex flex-col gap-4 mt-2">
              <div className="rounded-2xl p-6" style={{ border: '1px solid rgba(91,184,160,0.15)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <QrCode size={18} style={{ color: TEAL }} />
                  <h3 className="font-semibold text-base" style={{ color: DARK }}>1. Rögzíted az alapanyag-tételt</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  A Nyomonkövetés menüben megadod, melyik beszállítótól, milyen tétel- vagy számlaszámmal, mikor érkezett egy alapanyag. Ez néhány másodperc, minden beérkezéskor.
                </p>
              </div>
              <div className="rounded-2xl p-6" style={{ border: '1px solid rgba(91,184,160,0.15)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <Clock size={18} style={{ color: TEAL }} />
                  <h3 className="font-semibold text-base" style={{ color: DARK }}>2. Generálsz egy QR-kódot a gyártáshoz</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Kiválasztod a receptet és a gyártás dátumát — a rendszer automatikusan összegyűjti, melyik alapanyaghoz melyik legutóbb rögzített tétel tartozik, és legenerál egy QR-kódot, ami erre az adatra mutat. A gyártási és fogyaszthatósági dátum is rákerül.
                </p>
              </div>
              <div className="rounded-2xl p-6" style={{ border: '1px solid rgba(91,184,160,0.15)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <Check size={18} style={{ color: TEAL }} />
                  <h3 className="font-semibold text-base" style={{ color: DARK }}>3. Ráteszed a címkére, kész</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Az <Link to="/gyartmanylap" style={{ color: TEAL, fontWeight: 600 }}>etikett-címkén</Link> egy checkbox-szal bekapcsolható a QR-kód — a normál nyomtatóddal kinyomtatva a vevő vagy az ellenőr a saját telefonjával be tudja olvasni.
                </p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-bold mb-4" style={{ color: DARK }}>Opcionális: számla-fotó tárolás</h2>
            <p className="text-base leading-relaxed text-gray-600">
              Ha szeretnéd, a beszállítói számlát is lefotózhatod telefonnal minden tételhez, és a rendszer eltárolja — így ha később bárki visszakérdez egy adott beszerzésre, nem kell papír között keresgélni. Ez erőforrás-igénye miatt külön, kis összegű havi bővítmény (<Link to="/elofizetes" style={{ color: TEAL, fontWeight: 600 }}>990 Ft + ÁFA/hó</Link>) — az alap tétel-rögzítés és QR-generálás enélkül is teljes értékű.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold mb-6" style={{ color: DARK }}>Összefoglaló checklist</h2>
            <div className="rounded-2xl p-6" style={{ background: BG, border: '1px solid rgba(91,184,160,0.2)' }}>
              <ul className="flex flex-col gap-3">
                {[
                  'Minden alapanyag-beérkezéskor rögzítsd a beszállítót és a tétel/számla számot',
                  'Gyártás előtt/után generálj QR-kódot a recepthez és a gyártás dátumához',
                  'Az etikett-nyomtatásnál pipáld be a QR-kód opciót',
                  'Ha szeretnéd a számlákat is megőrizni, kapcsold be a számla-fotó bővítményt',
                ].map(item => (
                  <li key={item} className="flex items-start gap-3">
                    <Check size={18} style={{ color: TEAL }} className="mt-0.5 shrink-0" />
                    <span className="text-sm text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

        </div>
      </article>

      <section style={{ background: BG }} className="py-20">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-10" style={{ color: DARK }}>Gyakori kérdések</h2>
          <FaqAccordion items={FAQS} />
        </div>
      </section>

      <section style={{ background: DARK }} className="py-20">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-6"
            style={{ background: 'rgba(91,184,160,0.15)', border: '1px solid rgba(91,184,160,0.25)' }}>
            <Camera size={26} style={{ color: TEAL }} />
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">Próbáld ki ingyen</h2>
          <p className="mb-8 text-base" style={{ color: 'rgba(255,255,255,0.55)' }}>A nyomonkövetés alapfunkciói (tétel rögzítés, QR-generálás) minden csomagban benne vannak.</p>
          <Link to="/register" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-base" style={{ background: TEAL, color: '#fff' }}>
            Ingyenes regisztráció <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
